/**
 * Date utility functions for messaging — extracted from MessageTimestamp.tsx
 */

/** Check whether two dates fall on the same calendar day */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Check whether `target` is yesterday relative to `now` */
export function isYesterday(target: Date, now: Date): boolean {
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  return isSameDay(target, yesterday);
}

/** Short Hebrew day names indexed by getDay() (0=Sun) */
const HE_DAYS = ['יום א׳', 'יום ב׳', 'יום ג׳', 'יום ד׳', 'יום ה׳', 'יום ו׳', 'שבת'] as const;
const EN_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

const HE_MONTHS = [
  'בינו׳', 'בפבר׳', 'במרץ', 'באפר׳', 'במאי', 'ביוני',
  'ביולי', 'באוג׳', 'בספט׳', 'באוק׳', 'בנוב׳', 'בדצמ׳',
] as const;
const EN_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/**
 * Format a date for a date separator label.
 * Returns: "Today" / "Yesterday" / "Mon" / "Jan 15"
 */
export function formatDateSeparator(
  date: Date,
  lang: string,
  t: (key: string) => string,
): string {
  const now = new Date();
  const isHe = lang === 'he';

  if (isSameDay(date, now)) {
    return t('messaging.dateSeparator.today');
  }

  if (isYesterday(date, now)) {
    return t('messaging.dateSeparator.yesterday');
  }

  // Within last 7 days — show day name
  const sixDaysAgo = new Date(now);
  sixDaysAgo.setDate(sixDaysAgo.getDate() - 6);
  sixDaysAgo.setHours(0, 0, 0, 0);
  if (date >= sixDaysAgo) {
    return isHe ? HE_DAYS[date.getDay()] : EN_DAYS[date.getDay()];
  }

  // Older — "Jan 15" / "15 בינו׳"
  const day = date.getDate();
  const month = isHe ? HE_MONTHS[date.getMonth()] : EN_MONTHS[date.getMonth()];
  return isHe ? `${day} ${month}` : `${month} ${day}`;
}
